import generateLocalStorageKey from './generateLocalStorageKey'

// Canonical type strings; see `presentationOptionSchema.type` in
// QueryBlueprintDefinition.js. Mismatch breaks both Joi and EDITOR_MAP.
export const PRESENTATION_TYPE_OPTIONS = [
  { value: 'Data Grid', label: 'Data Grid' },
  { value: 'Presentation Table', label: 'Presentation Table' },
  { value: 'Hierarchical Table', label: 'Hierarchical Table' },
  { value: 'Hierarchical Grid', label: 'Hierarchical Grid' },
  { value: 'Hierarchical Pivot Table', label: 'Hierarchical Pivot Table' },
  { value: 'Metric', label: 'Metric' },
  { value: 'Chart', label: 'Chart' },
  { value: 'Scope Selector', label: 'Scope Selector' }
]

const TYPE_VALUES = new Set(PRESENTATION_TYPE_OPTIONS.map(o => o.value))

export function isCanonicalPresentationType (type) {
  return TYPE_VALUES.has(type)
}

export function presentationTypeSlug (type) {
  if (!type) return 'option'
  return String(type)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'option'
}

// Seeds Joi-aligned defaults so type editors render inputs for every required key.
// Required fields stay null until the user fills them in; save validates.
export function defaultConfigForType (type) {
  switch (type) {
    case 'Metric':
      return {
        purposeCode: null,
        accessor: null,
        title: null,
        primaryFormat: null,
        additionalFormats: [],
        icon: null,
        footer: null,
        size: 'md',
        align: 'left'
      }

    case 'Chart':
      return {
        layout: 'axis',
        purposeCode: null,
        height: 400,
        colorTheme: 'primary',
        xAxis: { type: 'category', accessor: null, format: null, name: null, position: 'bottom', boundaryGap: null },
        yAxes: [{ axisId: 'left', type: 'value', format: null, name: null, position: 'left', padFactor: 0 }],
        tooltip: { trigger: 'axis', valueFormat: null },
        series: []
      }

    case 'Hierarchical Table':
      return {
        purposeCode: null,
        columns: [],
        hierarchy: {
          levels: [],
          defaultExpandDepth: 1,
          aggregation: { mode: 'none', columnAggregates: [], depthColumn: null },
          labelColumn: '_hierarchyLabel',
          leafLabelAccessor: null
        },
        footerPurposeCode: null,
        footerColumnMapping: [],
        defaultSort: [],
        export: { enabled: false, formats: [], defaultFileName: null }
      }

    case 'Hierarchical Grid':
      return {
        columns: [],
        hierarchy: {
          levels: [],
          initialFetchDepth: 1,
          defaultExpandDepth: 0,
          labelColumn: '_hierarchyLabel',
          leafLabelAccessor: null
        },
        defaultSort: [],
        localStorageKey: generateLocalStorageKey(),
        variationGroups: ['default'],
        latchMode: 'selectable',
        enableHierarchicalFilter: true,
        export: { enabled: false, formats: [], defaultFileName: null, flattenOnExport: true }
      }

    case 'Hierarchical Pivot Table':
      return {
        purposeCode: null,
        columns: [],
        hierarchy: {
          levels: [],
          defaultExpandDepth: 1,
          labelColumn: '_hierarchyLabel',
          leafLabelAccessor: null
        },
        pivot: {
          pivotColumn: '',
          valueColumn: '',
          aggregateFunction: 'sum',
          showTotal: true,
          showAllocation: false,
          sortPivotColumns: 'alpha',
          enableColumnGrouping: false,
          groupColumn: null
        },
        footerPurposeCode: null,
        footerColumnMapping: [],
        defaultSort: [],
        export: { enabled: false, formats: [], defaultFileName: null }
      }

    case 'Scope Selector':
      return {
        mode: 'membership',
        purposeCode: null,
        scopes: [],
        display: 'simple',
        multiSelect: false,
        hideAvatars: false,
        size: 'sm'
      }

    case 'Presentation Table':
    case 'Data Grid':
    default:
      return {
        purposeCode: null,
        columns: [],
        footerPurposeCode: null,
        footerColumnMapping: [],
        defaultSort: [],
        localStorageKey: generateLocalStorageKey(),
        variationGroups: ['default'],
        topN: null
      }
  }
}
